/**
 * Which message files this app can OPEN, as opposed to save (#1069 Story 4.2).
 *
 * ⚠️ ONE RULE, ONE PLACE. The panel uses it to mark a gallery item renderable
 * and the bubble uses it to decide whether a chip is pressable at all; spelling it twice is
 * how the two drift and a file becomes tappable into nothing — which is the
 * dead control the owner hit on a `.pptx` (2026-09-14).
 *
 * ⚠️ IT READS THE STORED (SNIFFED) TYPE, never the file name. A PDF that
 * claimed `image/png` is a PDF here, which is the whole reason Story 4.1
 * sniffs.
 */
import type { MessageFile } from '../../components/correspondence/correspondence.types';

/**
 * ⚠️ A PER-TYPE MODAL ROUTER LIVED HERE UNTIL GITLAB #1539. Every file now opens the one
 * gallery (`AttachmentCarouselModal`), which picks a renderer per item from
 * the stored type itself; the panel no longer routes to a modal by type. What
 * stays is the one rule below: which files cost a fetch.
 *
 * ⚠️ EVERY FILE STILL OPENS SOMETHING (owner + UX consult, 2026-09-14): a
 * file this app cannot preview opens the gallery's UNSUPPORTED page -- what
 * the file is, how big, and a Download -- never an inert chip.
 */

/**
 * The image types the app can actually put on a screen, on every platform it
 * runs on.
 *
 * ⚠️ AN ALLOWLIST, NOT `startsWith('image/')` (code review, 2026-09-14). The
 * gate below exists to make one promise: a file that cannot be rendered does
 * not cost a download. `startsWith('image/')` broke that promise for the
 * formats most likely to arrive from a phone, because the sniffer deliberately
 * recognises more image types than any renderer here accepts, and nothing
 * refuses them at selection (`BLOCKED_EXECUTABLE_EXTENSIONS` is a blocklist
 * whose own copy invites "documents, images, videos, or audio files"):
 *
 *  · `image/heic` / `image/heif` — a default-format iPhone photo. Renders in
 *    Safari and in native `Image`, and fails in Chrome and Firefox.
 *  · `image/tiff` — decodes on the server, fails on web and on Android.
 *  · `image/avif` — decodes on the server and renders in modern browsers, but
 *    native `Image` support starts at iOS 16 / Android 12. Left out because
 *    this list has to hold on every platform the app ships to; a tile that
 *    opens nothing on an older phone is the dead control this story removed.
 *
 * All of them open the unsupported dialog, which names the file, its type and
 * its size and offers Download. That is a true statement on every platform and
 * costs no bytes.
 *
 * ⚠️ AN EARLIER VERSION OF THIS COMMENT SAID THE FIX WAS TO TRANSCODE AT THE
 * SERVING ROUTE, "since the thumbnail path already proves the server can decode
 * HEIC". THAT WAS WRONG, and measuring it is what showed why (2026-09-14).
 * `sharp.format.heif` here means AVIF — its `fileSuffix` is `['.avif']` — and
 * the prebuilt binary carries libheif with AV1 only, no HEVC decoder. A real
 * HEIC put through `createInlineImageThumbnail` fails with "heif: Decoder
 * plugin generated an error", which is also why such a file had no thumbnail
 * and rendered as a blank tile. The server cannot transcode what it cannot
 * decode. The fix went where the decoder actually exists: the picker converts
 * on the device (`messageFilePicker.native.ts`), and anything undecodable that
 * still arrives is served as a CHIP rather than an empty tile
 * (`correspondenceService.toWireFile`).
 */
const RENDERABLE_IMAGE_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
]);

/**
 * Can this file actually be RENDERED, as opposed to merely opened?
 *
 * The distinction matters for one reason: a file that cannot be rendered must
 * not have its bytes fetched just to say so. The dialog opens from metadata
 * and fetches only if Download is pressed.
 *
 * ⚠️ READS THE STORED (SNIFFED) TYPE. A `Set` rather than an
 * object literal, per `.claude/rules/correspondence.md`: these keys are
 * derived from user-supplied bytes, and an object literal resolves
 * `constructor` on `Object.prototype`.
 */
export const canPreview = (file: MessageFile): boolean =>
  RENDERABLE_IMAGE_TYPES.has(file.mimeType) ||
  file.mimeType === 'application/pdf';
